import React from "react"

import logo from "images/brand/logos/short.svg"
import { FiMapPin } from "react-icons/fi"
import Tooltip from "design_system/core/components/tooltip"
import ContentCloser from "@love-irish/application-content-closer"
import routes from "@love-irish/routes"
import analytics from "@love-irish/analytics"
import {
  IconBrandBluesky,
  IconBrandDiscord,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandYoutube
} from "@tabler/icons-react";
import DynamicDatetimeLabel from "@love-irish/application-dynamic-datetime-label";

import { ShopSection } from "@love-irish/application-homepage-template"
import { ConnectedDailyScene } from "@love-irish/application-daily-scene-template"
import NewsletterSection from './newsletter_section'
import FeedbackModal from "@love-irish/application-feedback-modal";
import { useSession } from "@love-irish/application-session-provider";

const resourceLinks = [
  { name: "Calendar", href: routes.calendarPath(), translation: "Ceiléir" },
  { name: "Topics", href: routes.topicsPath(), translation: "Ábhair" },
  { name: "Collections", href: routes.collectionsPath(), translation: "Bailiúcháin" },
  { name: "Codex", href: routes.codexPath(), translation: "Códex" },
  { name: "Interviews", href: routes.interviewsPath(), translation: "Agallaimh" },
  { name: "TV (Cast screen)", href: routes.tvPath(), translation: "Teilifís" },
]

const adventureLinks = [
  { name: "Arcade", href: routes.arcadePath(), translation: "Teach imirt" },
  { name: "Stories", href: routes.storiesPath(), translation: "Scéalta" },
  { name: "Inventory", href: routes.inventoryPath(), translation: "Fardal" },
  { name: "Leaderboard", href: routes.leaderboardPath(), translation: "Clár ceann riain" },
  { name: "Rewards", href: routes.adventureRewardsPath(), translation: "Duaiseanna" },
]

const shopLinks = [
  { name: "T-shirts", href: 'https://shop.love.irish/collections/t-shirts', translation: "T-leinte" },
  { name: "Hoodies", href: 'https://shop.love.irish/collections/hoodies', translation: "Geansai" },
  { name: "Mens", href: 'https://shop.love.irish/collections/men', translation: "Fear" },
  { name: "Womens", href: 'https://shop.love.irish/collections/women', translation: "Bean" },
  { name: "Everything", href: 'https://shop.love.irish/collections/all', translation: "Gach rud" },
]

const communityLinks = [
  // { name: "Directory", href: routes.interviewsPath(), translation: "Díolachán" },
  { name: "Guestbook", href: '/guestbook', translation: "Leabhar Aíochta" },
  // { name: "Profiles", href: routes.interviewsPath(), translation: "Próifílí" },
  // { name: "Profile bling", href: routes.interviewsPath(), translation: "Bling próifíl" },
]

const libraryLinks = []

const supportLinks = [
  // { name: "Community guidelines", href: routes.sitemapPath() },
  { name: "Changelog", href: routes.changelogPath(), translation: "Loga"},
  { name: "FAQ", href: routes.faqPath(), translation: "Ceisteanna Coitianta" },
  { name: "Signup", href: '/signup', translation: "Cruthaigh cuntas" },
  { name: "Sitemap", href: '/sitemap.xml', translation: "Léarscáil" },
  { name: "Privacy", href: '/privacy', translation: "Príobháideachas" },
  { name: "Terms", href: '/terms', translation: "Teorainneacha" },
  { name: "Contact", href: '/contact', translation: "Teagmháil" },
]

const companyLinks = [
  { name: "Home", href: routes.homePath(), translation: "Baile" },
  { name: "About us", href: routes.aboutUsPath(), translation: "Fúinn" },
  // { name: "Artists", href: routes.aboutUsPath(), translation: "Fúinn" },
  // { name: "Blog", href: routes.blogPostsPath(), translation: "Blag" },
]

const AppFooter = ({ currentUser, currentPath }) => {
  // eslint-disable-next-line camelcase
  const handleScrollToTopClick = () =>
    analytics.track("clicked#scroll_to_top", { link_location: ["footer"] })
  const handleLinkClick = ({ label, href, section }) =>
    analytics.trackLinkClick({ label, href, location: ["footer", section] })

  const { loggedIn } = useSession()

  // The homepage already renders DailyScene in its body and keeps the merch promo here. Every other
  // page gets the time-of-day scene (full-bleed, above the container) in place of the merch promo.
  const isHomepage = !currentPath || currentPath === "/" || currentPath === routes.homePath()

  return (
    <footer className="grow-0 mt-12">

      <div className="container">
        <div className="my-6">
          <hr className="w-full border-0 border-t border-gray-300 block" />
        </div>

        {isHomepage && (
          <div className="container my-24">
            <ShopSection />
          </div>
        )}

        {!isHomepage && (
          <div className="my-24">
            <ConnectedDailyScene slug="irish-throughout-the-day" />
          </div>
        )}

        {!loggedIn && (
          <>
            <div className="my-6">
              <hr className="w-full border-0 border-t border-gray-300" />
            </div>

            <div className="container my-12">
              <NewsletterSection />
            </div>
          </>
        )}

        <ContentCloser scrollToTop onScrollClick={handleScrollToTopClick} />

        <div className="grid gap-12 md:grid-cols-4 pb-10">
          <div className="md:col-span-1 col-span-2 text-sm">
            <a href="/"><img width={50} src={logo} alt="Love.irish logo" /></a>

            <p>
              <Tooltip
                title="Broken Irish is better than clever English️"
                wrapperClass="inline"
              >
                <em className="text-definition">
                  Is fearr Gaeilge bhriste ná Béarla cliste
                </em>
              </Tooltip>{" "}
              &middot; The best way to get better at something is to practice so
              don&rsquo;t worry about being perfect!
            </p>

            <p className="mt-4">
              This site is run by and for fans and learners — corrections welcome ❤️
            </p>

            <p className="mt-8 text-gray-800">
              <FiMapPin className="inline" /> Based in the{" "}
              <strong>San Francisco Bay Area</strong>
            </p>
            <p className="mt-2">4101 Dublin Blvd, Suite F #1038, <br/>Dublin, CA 94568</p>

            <p className="mt-8">
              &copy; {new Date().getFullYear()} Love.irish. All rights
              reserved.
            </p>

            <div className="text-gray-400 mt-8 flex gap-3">
              <a href="https://discord.gg/BXHy8hbRYP" target="_blank" rel="noopener noreferrer">
                <IconBrandDiscord size={24} className="inline" />
              </a>
              <a href="https://www.youtube.com/@love-irish" target="_blank" rel="noopener noreferrer">
                <IconBrandYoutube size={24} className="inline" />
              </a>
              <a href="https://bsky.app/profile/love.irish" target="_blank" rel="noopener noreferrer">
                <IconBrandBluesky size={24} className="inline" />
              </a>
              <a href="https://www.facebook.com/loveirishapp" target="_blank" rel="noopener noreferrer">
                <IconBrandFacebook size={24} className="inline text-right" />
              </a>
              <a href="https://www.instagram.com/love.irish.app" target="_blank" rel="noopener noreferrer">
                <IconBrandInstagram size={24} className="inline" />
              </a>
            </div>
          </div>
          <div className="grid gap-3 grid-cols-2  col-span-2 md:grid-cols-5  md:col-span-3">
            <div>
              <h3 className="text-lg font-serif">
              <a href="/community">Community</a>
              </h3>
              <p className="text-sm mb-3 text-gray-500">
                <em>Pobal</em>
              </p>
              <ul className="space-y-1">
                {communityLinks.map(({ name, href, translation }) => (
                  <li key={name}>
                    <a
                      href={href}
                      onClick={() =>
                        handleLinkClick({
                          label: name,
                          section: "products",
                          href,
                        })
                      }
                    >
                      {name}{" "}
                      <em className="text-gray-400 text-xs">{translation}</em>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-serif">
                <a href={routes.resourcesPath()}>Resources</a>
              </h3>
              <p className="text-sm mb-3 text-gray-500">
                <em>Seachaidh</em>
              </p>
              <ul className="space-y-1">
                {resourceLinks.map(({ name, href, translation }) => (
                  <li key={name}>
                    <a
                      href={href}
                      onClick={() =>
                        handleLinkClick({
                          label: name,
                          section: "products",
                          href,
                        })
                      }
                    >
                      {name}{" "}
                      <em className="text-gray-400 text-xs">{translation}</em>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-serif">
                <a href="/adventure">Adventure</a>
              </h3>
              <p className="text-sm mb-3 text-gray-500">
                <em>Eachtra</em>
              </p>
              <ul className="space-y-1">
                {adventureLinks.map(({ name, href, translation }) => (
                  <li key={name}>
                    <a
                      href={href}
                      onClick={() =>
                        handleLinkClick({
                          label: name,
                          section: "products",
                          href,
                        })
                      }
                    >
                      {name}{" "}
                      <em className="text-gray-400 text-xs">{translation}</em>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-serif">
                <a href="https://shop.love.irish" target="_blank">Shop</a>
              </h3>
              <p className="text-sm mb-3 text-gray-500">
                <em>Siopa</em>
              </p>
              <ul className="space-y-1">
                {shopLinks.map(({ name, href, translation }) => (
                  <li key={name}>
                    <a
                      href={href}
                      onClick={() =>
                        handleLinkClick({
                          label: name,
                          section: "products",
                          href,
                        })
                      }
                    >
                      {name}{" "}
                      <em className="text-gray-400 text-xs">{translation}</em>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-serif  mb-2">Company</h3>
              <ul>
                {companyLinks.map(({ name, href, translation }) => (
                  <li key={name}>
                    <a
                      href={href}
                      onClick={() =>
                        handleLinkClick({ label: name, section: "company", href })
                      }
                    >
                      {name}{" "}
                      <em className="text-gray-400 text-xs">{translation}</em>
                    </a>
                  </li>
                ))}
              </ul>

              <h3 className="text-lg font-serif mb-2 mt-6">Help &amp; support</h3>
              <ul className="space-y-1">
                {supportLinks.map(({ name, href, translation }) => (
                  <li key={name}>
                    <a
                      href={href}
                      onClick={() =>
                        handleLinkClick({ label: name, section: "support", href })
                      }
                    >
                      {name}{" "}
                      <em className="text-gray-400 text-xs">{translation}</em>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-5">
                <FeedbackModal />
              </div>

            </div>
          </div>
        </div>

        <hr className="mt-7 mb-3" />

        <div className="flex justify-between my-5 text-sm">
          <div>
            <DynamicDatetimeLabel currentUser={currentUser} />
          </div>
          <small>
            <em>
              <Tooltip title="I am a footer 🦶" wrapperClass="text-definition font-normal!">
                Is footer mé
              </Tooltip>
            </em>
          </small>
        </div>
      </div>
    </footer>
  )
}


export default AppFooter
